/**
 * Renders a multi-option button selection block.
 *
 * Displays a title, optional description, and an array of clickable button cards.
 * Single-select: clicking one option submits immediately (like single-question AskUserQuestion).
 * Each button can show a label, optional description, and optional icon emoji.
 *
 * Uses Deckard's theme palette tokens for proper light/dark mode support.
 */
import { Group } from '@components/layout';
import { Typography } from '@components/typography';
import { Box, Stack, useTheme } from '@mui/material';
import type { RichBlockProps } from './richBlockTypes';

export const ButtonsBlock = ({ block, onRespond }: RichBlockProps) => {
  const { palette } = useTheme();
  // Only block interaction when explicitly answered — expired blocks remain
  // clickable so the user can re-send their selection as a new message.
  const isAnswered = block.status === 'answered';
  const answeredSelection = (block.response as { selection?: string } | undefined)?.selection;
  const {
    title,
    description,
    options = [],
  } = block.payload as {
    title?: string;
    description?: string;
    options: Array<{ label: string; description?: string; icon?: string }>;
  };

  const handleClick = (label: string) => {
    if (isAnswered) return;
    onRespond?.(block.blockId, { selection: label });
  };

  return (
    <Box mt={1} mb={2} data-testid="rich-block-buttons">
      {title && (
        <Typography variant="subtitle1" color="agentChat.message.agent.foreground.default">
          {title}
        </Typography>
      )}
      {description && (
        <Typography
          variant="caption2"
          sx={{ color: palette.text.light, display: 'block', mb: 1.5, lineHeight: 1.5 }}
        >
          {description}
        </Typography>
      )}
      <Stack gap={1}>
        {options.map((opt) => {
          const isSelected = answeredSelection === opt.label;

          return (
            <Group
              key={opt.label}
              role="button"
              onClick={() => handleClick(opt.label)}
              sx={{
                p: 1,
                cursor: isAnswered ? 'default' : 'pointer',
                opacity: isAnswered && !isSelected ? 0.5 : 1,
                border: '1px solid',
                borderColor: isSelected ? palette.primary.dark : palette.input.border.default,
                bgcolor: isSelected ? palette.primary.main : palette.background.paper,
                borderRadius: 1.5,
                transition: 'all 0.15s',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                '.agent-chat-buttons-block-label': {
                  color: isSelected
                    ? palette.common.black
                    : palette.agentChat.message.agent.foreground.default,
                },
                '.agent-chat-buttons-block-description': {
                  color: isSelected
                    ? palette.primary.contrastText
                    : palette.agentChat.message.agent.foreground.light,
                },
                '&:hover': isAnswered
                  ? {}
                  : {
                      bgcolor: palette.agentChat.message.agent.backgroundHover,
                    },
              }}
              data-testid={`buttons-option-${opt.label}`}
            >
              {/* Optional icon emoji rendered as a large character */}
              {opt.icon && (
                <Typography sx={{ fontSize: '1.25rem', lineHeight: 1, flexShrink: 0 }}>
                  {opt.icon}
                </Typography>
              )}
              <Box>
                <Typography variant="body1" className="agent-chat-buttons-block-label">
                  {opt.label}
                </Typography>
                {opt.description && (
                  <Typography variant="caption2" className="agent-chat-buttons-block-description">
                    {opt.description}
                  </Typography>
                )}
              </Box>
            </Group>
          );
        })}
      </Stack>
    </Box>
  );
};
